import { AppConfig } from './app-config';

/**
 * This class allows for the runtime config to be overwritten by an object
 * read from the `'config'` key of session storage. This allows developers and testers
 * to exercise environment specific configurations in a deployed environment that is
 * not the deployment environment associated with the expected remote config.
 *
 * Intended to be used in `main.ts`
 */
export class SessionConfigLoader {
    private sessionStorage: Storage;
    private consoleRef?: Console;

    /**
     * Constructor.
     *
     * Passing in a null console reference has the effect of silencing console output in this class.
     */
    constructor(storageRef: Storage, consoleRef: Console | null = console) {
        this.sessionStorage = storageRef;
        // using console rather than a logger because this code is intended to be used
        // prior to Angular application bootstrapping.
        this.consoleRef = consoleRef ?? undefined;
    }

    mapRuntimeConfig(runtimeConfig: AppConfig): AppConfig {
        const sessionConfig: Partial<AppConfig> | undefined = this.getSessionConfig();
        return this.mergeConfigs(runtimeConfig, sessionConfig);
    }

    private getSessionConfig(): Partial<AppConfig> | undefined {
        const sessionConfigString = this.sessionStorage.getItem('config');
        if (!sessionConfigString) {
            return;
        }
        try {
            return JSON.parse(sessionConfigString) as AppConfig;
        } catch (error) {
            this.consoleRef?.error('Unable to parse app config from Session Storage', error);
        }
        return undefined;
    }

    private mergeConfigs(runtimeConfig: AppConfig, sessionConfig?: Partial<AppConfig>): AppConfig {
        if (!sessionConfig) {
            return runtimeConfig;
        }

        this.consoleRef?.log('Original config', { ...runtimeConfig });
        const updatedConfig = {
            ...runtimeConfig,
            ...sessionConfig,
            featureFlags: {
                ...runtimeConfig.featureFlags,
                ...sessionConfig.featureFlags,
            },
        };
        this.consoleRef?.log('Updated config', updatedConfig);
        return updatedConfig;
    }
}
